// Name labels drawn as HTML over the 3D view: cave names, marker names, USGS place names and survey stations.
// Each redraw projects every candidate to the screen, ranks them (caves > features > place names > observations >
// stations, nearer first) and keeps only those that don't overlap a label already placed.
import * as THREE from 'three';

const STYLE = {                           // css class, font (for measuring), height (px), rank
  cave: { cls: 'lbl cave', font: '700 14px system-ui, sans-serif', h: 18, rank: 100 },
  feature: { cls: 'lbl feature', font: '600 12px system-ui, sans-serif', h: 16, rank: 70 },
  place: { cls: 'lbl place', font: '600 12px system-ui, sans-serif', h: 16, rank: 55 },
  relief: { cls: 'lbl relief', font: 'italic 600 12px system-ui, sans-serif', h: 16, rank: 50 },
  natcave: { cls: 'lbl relief', font: 'italic 600 12px system-ui, sans-serif', h: 16, rank: 50 },
  water: { cls: 'lbl water', font: 'italic 600 11.5px system-ui, sans-serif', h: 15, rank: 40 },
  observation: { cls: 'lbl obs', font: '500 11.5px system-ui, sans-serif', h: 15, rank: 35 },
  record: { cls: 'lbl record', font: 'italic 600 11.5px system-ui, sans-serif', h: 15, rank: 33 },
  study_point: { cls: 'lbl obs', font: '500 11.5px system-ui, sans-serif', h: 15, rank: 35 },
  passage: { cls: 'lbl passage', font: 'italic 700 12.5px system-ui, sans-serif', h: 17, rank: 60 },
  cplace: { cls: 'lbl cplace', font: '700 12.5px system-ui, sans-serif', h: 17, rank: 62 },
  cnote: { cls: 'lbl cnote', font: '500 11px system-ui, sans-serif', h: 14, rank: 12 },
  stream: { cls: 'lbl stream', font: 'italic 700 12px system-ui, sans-serif', h: 16, rank: 58 },
  covecave: { cls: 'lbl cave cove', font: '700 13px system-ui, sans-serif', h: 17, rank: 95 },
  spring: { cls: 'lbl spring', font: '700 12px system-ui, sans-serif', h: 16, rank: 75 },
  basin: { cls: 'lbl basin', font: 'italic 700 13px system-ui, sans-serif', h: 17, rank: 45 },
  parcel: { cls: 'lbl parcel', font: '600 11.5px system-ui, sans-serif', h: 15, rank: 30 },
  measure: { cls: 'lbl measure', font: '700 12px system-ui, sans-serif', h: 16, rank: 200 },
  lead: { cls: 'lbl lead', font: '800 13px system-ui, sans-serif', h: 16, rank: 20 },
  station: { cls: 'lbl station', font: '500 10.5px ui-monospace, Menlo, Consolas, monospace', h: 14, rank: 10 },
};
export const normName = (s) => String(s || '').toLowerCase().replace(/['’]s\b/g, 's').replace(/[^a-z0-9]+/g, ' ').trim().replace(/^the /, '');
const NAME_GROUP = { relief: 'relief', water: 'water', place: 'place', cave: 'natcave' };
const MAX_LABELS = 260, STATION_RANGE = 450, CELL = 48;

export class Labels {
  constructor(viewer) {
    this.v = viewer;
    this.root = document.createElement('div'); this.root.className = 'labels'; this.root.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.root);
    this.root.addEventListener('click', (e) => {
      const d = e.target.dataset || {};
      if (viewer.labelPick) {                                // making a profile label: the label's own name and place
        const c = e.target._cand; if (!c) return;
        const [xc, yc, zc] = viewer.origin;                // (underground labels already sit at the shifted datum, as the profile does)
        viewer.labelPick({ kind: c.kind, text: c.text, E: c.x + xc, N: yc - c.z, Z: c.y + zc });
        e.stopPropagation(); return;
      }
      if (d.cave) viewer.emit('select', { type: 'cave', id: +d.cave });
      else if (d.place) viewer.emit('select', { type: 'place', id: +d.place });
    });
    this.pool = []; this.widths = new Map(); this.names = []; this.caves = []; this.mapLabels = []; this.measure = []; this.profile = []; this.parcels = []; this.hydro = []; this.water = []; this.cove = []; this.places = []; this.placeNames = new Set();
    this.ctx = document.createElement('canvas').getContext('2d');
    this.pv = new THREE.Matrix4(); this.tmp = new THREE.Vector3();
  }

  width(text, st) {
    const k = st.font + '|' + text;
    let w = this.widths.get(k);
    if (w == null) { this.ctx.font = st.font; w = Math.ceil(this.ctx.measureText(text).width); this.widths.set(k, w); }
    return w;
  }

  // USGS place names: [{name, group, e, n, z}]
  setNames(list) {
    const o = this.v.origin;
    this.names = o ? list.map(x => ({ text: x.name, kind: NAME_GROUP[x.group] || 'place', cls: x.cls, x: x.e - o[0], z: -(x.n - o[1]),
      y: x.z != null ? x.z - o[2] : null })) : [];
    this.v.invalidate();
  }

  setParcels(list) { this.parcels = list || []; this.v.invalidate(); }

  setHydro(list) { this.hydro = list || []; this.v.invalidate(); }
  setWater(list) { this.water = list || []; this.v.invalidate(); }
  // the cove caves overlay: [{text, kind, layer (the opts key that shows it), x, y?, z}]
  setCove(list) { this.cove = list || []; this.v.invalidate(); }

  // named places in caves: [{id, text, kind: cplace|cnote, top, x, y, z (cave-local), norm}]
  setPlaces(list) {
    this.places = list || [];
    this.placeNames = new Set(this.places.filter(p => p.kind === 'cplace').map(p => p.norm).filter(Boolean));
    this.v.invalidate();
  }

  setMeasure(list) { this.measure = list || []; this.v.invalidate(); }
  setProfile(list) { this.profile = list || []; this.v.invalidate(); }

  // passage names, entrances and "?" leads read from the drawn (vector) maps; p = cave-local [x, y(up), z]
  setMapLabels(list) {
    this.mapLabels = (list || []).map(l => ({ text: l.k === 'lead' ? '?' : l.k === 'entrance' ? 'Entrance' : l.t,
      kind: l.k === 'lead' ? 'lead' : 'passage', x: l.p[0], y: l.p[1], z: l.p[2] }));
    this.v.invalidate();
  }

  // one label per cave, over its main entrance (an Entrance place of that cave if there is one, else its shallowest station)
  setCaves() {
    const v = this.v, M = v.M;
    this.caves = [];
    if (!M) return;
    const best = new Map();
    for (let i = 0; i < v.names.length; i++) {
      const c = M.surveys[v.stSurvey[i]]?.cave_id; if (c == null) continue;
      const d = v.stGround[i] - v.stPos[3 * i + 1], b = best.get(c);
      if (!b || d < b.d) best.set(c, { d, x: v.stPos[3 * i], z: v.stPos[3 * i + 2] });
    }
    for (const p of v.places) {
      if (p.type !== 'feature' || p.kind !== 'entrance' || p.cave_id == null || !best.has(p.cave_id)) continue;
      const b = best.get(p.cave_id); if (b.fromPlace) continue;
      const pl = v.placeLocal(p); if (pl) best.set(p.cave_id, { x: pl.v.x, z: pl.v.z, fromPlace: true });
    }
    const approx = new Map();                       // a cave whose surveys are all traced from a map is labelled "(approx.)"
    for (const s of M.surveys) approx.set(s.cave_id, (approx.get(s.cave_id) ?? true) && !!s.approx);
    for (const c of M.caves) { const b = best.get(c.id); if (b) this.caves.push({ text: c.name + (approx.get(c.id) ? ' (approx.)' : ''), kind: 'cave', id: c.id, x: b.x, z: b.z }); }
    v.invalidate();
  }

  clear() { for (const el of this.pool) if (el.style.display !== 'none') el.style.display = 'none'; }

  update() {
    const v = this.v, on = v.opts.labels || {};
    if ((!Object.values(on).some(Boolean) && !this.measure.length && !this.profile.length) || !v.origin) { this.clear(); return; }
    const cam = v.camera, ve = v.opts.ve, sh = v.opts.shift, W = innerWidth, H = innerHeight;
    this.pv.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    const p = this.tmp, cands = [];
    const add = (text, kind, x, y, z, anchor, extra = 0, id = null) => {
      p.set(x, y * ve, z).applyMatrix4(this.pv);
      if (p.z > 1 || p.z < -1 || p.x < -1.2 || p.x > 1.2 || p.y < -1.2 || p.y > 1.2) return;
      cands.push({ text, kind, sx: (p.x + 1) / 2 * W, sy: (1 - p.y) / 2 * H, d: p.z, anchor, extra, id, x, y, z });
    };
    // the section slicer hides what it cuts away: underground labels always, surface ones when the ground is cut,
    // cave names (over their entrance) when the entrance is outside the box east-west or north-south
    const inside = v.clip ? (x, y, z) => v.inClipLocal(x, y, z) : () => true;
    const surf = v.clip && v.clipTerrain ? (x, z, y) => v.inClipLocal(x, y ?? v.groundLocal(x, z), z) : () => true;
    const plan = v.clip ? (x, z) => v.inClipLocal(x, null, z) : () => true;
    if (on.caves) {
      const f = v.filter;
      for (const c of this.caves) if ((!f.caves || f.caves.has(c.id)) && plan(c.x, c.z)) add(c.text, 'cave', c.x, v.groundLocal(c.x, c.z), c.z, 'above', 1, c.id);
    }
    for (const m of this.measure) add(m.text, 'measure', m.x, m.y, m.z, 'center');
    for (const m of this.profile) add(m.text, 'measure', m.x, m.y, m.z, 'center');
    if (on.parcels && v.opts.parcels) for (const q of this.parcels) if (surf(q.x, q.z)) add(q.text, 'parcel', q.x, v.groundLocal(q.x, q.z), q.z, 'center');
    if (on.hydro && v.opts.hydro) for (const q of this.hydro) if (surf(q.x, q.z)) add(q.text, q.kind, q.x, v.groundLocal(q.x, q.z), q.z, q.kind === 'spring' ? 'right' : 'center');
    if (on.cove) for (const q of this.cove) if (v.opts[q.layer] && surf(q.x, q.z, q.y)) add(q.text, q.kind, q.x, q.y ?? v.groundLocal(q.x, q.z), q.z, q.kind === 'covecave' ? 'above' : 'right');
    if (on.hydro && v.opts.waterRoutes) for (const q of this.water) if (inside(q.x, q.y + sh, q.z)) add(q.text, 'stream', q.x, q.y + sh, q.z, 'center');
    if (on.places) for (const q of this.places) {
      if (q.kind === 'cnote') continue;
      if (inside(q.x, q.y + sh, q.z)) add(q.text, 'cplace', q.x, q.y + sh, q.z, 'center', q.top ? 3 : 0, q.id);
    }
    if (on.notes && cam.position.distanceTo(v.controls.target) < STATION_RANGE * 2.2) {
      const tg = v.controls.target, tx = tg.x, ty = tg.y / ve, tz = tg.z, R2 = STATION_RANGE * STATION_RANGE;
      for (const q of this.places) {
        if (q.kind !== 'cnote' || (q.x - tx) ** 2 + (q.y - ty) ** 2 + (q.z - tz) ** 2 > R2) continue;
        if (inside(q.x, q.y + sh, q.z)) add(q.text, 'cnote', q.x, q.y + sh, q.z, 'dot', 0, q.id);
      }
    }
    const dup = on.places && this.placeNames.size ? (t) => this.placeNames.has(normName(t)) : () => false;
    for (const m of this.mapLabels) {
      if (m.kind === 'passage' && dup(m.text)) continue;     // the same name is a place now: show that one
      if ((m.kind === 'lead' ? on.leads : on.passages) && inside(m.x, m.y + sh, m.z)) add(m.text, m.kind, m.x, m.y + sh, m.z, 'center');
    }
    if (on.names) for (const n of this.names) if (surf(n.x, n.z, n.y)) add(n.text, n.kind, n.x, n.y ?? v.groundLocal(n.x, n.z), n.z, 'center');
    for (const m of v.markers) {
      const t = m.place.type, k = t === 'feature' ? 'features' : t === 'observation' ? 'observations' : t === 'record' ? 'records' : 'studies';
      if (!on[k] || !m.place.name) continue;
      const under = m.sprite.parent === v.caveMarkers, q = m.sprite.position;
      if (under ? !inside(q.x, q.y + sh, q.z) : !surf(q.x, q.z, q.y)) continue;
      add(m.place.name, t, q.x, q.y + (under ? sh : 0), q.z, 'right');
    }
    const F = v.surveyFocus;                                 // the survey open in the data panel: all its stations, first
    if (F && v.M) {
      const S = v.stPos;
      for (const i of F.stations) {
        const x = S[3 * i], y = S[3 * i + 1] + sh, z = S[3 * i + 2];
        if (inside(x, y, z)) add(v.names[i], 'station', x, y, z, 'dot', 55);
      }
    }
    // other stations: near the middle of the view when zoomed in, or everywhere (as many as fit) with stationsAll
    if (on.stations && v.M && (on.stationsAll || cam.position.distanceTo(v.controls.target) < STATION_RANGE * 2.2)) {
      const S = v.stPos, tg = v.controls.target, tx = tg.x, ty = tg.y / ve, tz = tg.z, R2 = on.stationsAll ? Infinity : STATION_RANGE * STATION_RANGE;
      for (let i = 0; i < v.names.length; i++) {
        if (v.vis && !v.vis[v.stSurvey[i]]) continue;          // only the surveys being shown
        if (F?.set.has(i)) continue;
        const x = S[3 * i], y = S[3 * i + 1] + sh, z = S[3 * i + 2];
        if ((x - tx) ** 2 + (y - ty) ** 2 + (z - tz) ** 2 > R2 || !inside(x, y, z)) continue;
        add(v.names[i], 'station', x, y, z, 'dot');
      }
    }
    cands.sort((a, b) => (STYLE[b.kind].rank + b.extra) - (STYLE[a.kind].rank + a.extra) || a.d - b.d);

    const grid = new Map(), placed = [];
    const hit = (x0, y0, x1, y1) => {
      for (let gx = Math.floor(x0 / CELL); gx <= Math.floor(x1 / CELL); gx++) for (let gy = Math.floor(y0 / CELL); gy <= Math.floor(y1 / CELL); gy++) {
        const cell = grid.get(gx * 4096 + gy); if (!cell) continue;
        for (const b of cell) if (x0 < b[2] && x1 > b[0] && y0 < b[3] && y1 > b[1]) return true;
      }
      return false;
    };
    const mark = (b) => {
      for (let gx = Math.floor(b[0] / CELL); gx <= Math.floor(b[2] / CELL); gx++) for (let gy = Math.floor(b[1] / CELL); gy <= Math.floor(b[3] / CELL); gy++) {
        const k = gx * 4096 + gy; (grid.get(k) || grid.set(k, []).get(k)).push(b);
      }
    };
    const side = W > 900 ? 356 : 0;                       // the side panel covers the left of the screen
    for (const c of cands) {
      if (placed.length >= MAX_LABELS) break;
      const st = STYLE[c.kind], w = this.width(c.text, st) + 6, h = st.h;
      let x0, y0;
      if (c.anchor === 'right') { x0 = c.sx + 13; y0 = c.sy - h / 2; }
      else if (c.anchor === 'dot') { x0 = c.sx + 5; y0 = c.sy - h / 2; }
      else if (c.anchor === 'above') { x0 = c.sx - w / 2; y0 = c.sy - h - 16; }
      else { x0 = c.sx - w / 2; y0 = c.sy - h / 2; }
      const b = [x0 - 2, y0 - 1, x0 + w + 2, y0 + h + 1];
      if (b[2] < side || b[0] > W || b[3] < 0 || b[1] > H || hit(b[0], b[1], b[2], b[3])) continue;
      mark(b); placed.push({ c, x0, y0, st });
    }
    for (let i = 0; i < placed.length; i++) {
      let el = this.pool[i];
      if (!el) { el = document.createElement('div'); this.root.appendChild(el); this.pool.push(el); }
      const { c, x0, y0, st } = placed[i];
      if (el._t !== c.text) { el.textContent = c.text; el._t = c.text; }
      el._cand = c;
      if (el._c !== st.cls) { el.className = st.cls; el._c = st.cls; }
      const tag = c.id == null ? null : c.kind === 'cave' ? 'c' + c.id : (c.kind === 'cplace' || c.kind === 'cnote') ? 'p' + c.id : null;
      if (el._id !== tag) {
        delete el.dataset.cave; delete el.dataset.place;
        if (tag?.[0] === 'c') el.dataset.cave = c.id; else if (tag) el.dataset.place = c.id;
        el._id = tag;
      }
      el.style.transform = `translate(${Math.round(x0)}px,${Math.round(y0)}px)`;
      if (el.style.display === 'none') el.style.display = '';
    }
    for (let i = placed.length; i < this.pool.length; i++) if (this.pool[i].style.display !== 'none') this.pool[i].style.display = 'none';
  }
}
